/**
 * Browser-fold — collapse an idle browser surface into the parked chip.
 *
 * The complaint this kills: once a task used the browser once, every later
 * turn revived the surface even when the turn never touched the browser (the
 * server's hibernate-resume re-mint converges an open pane to `live` on every
 * wake — see apps/server relay/browser-session.ts). The fix converts a level
 * back into an edge at the cheapest point: the client releases pane demand at
 * the moment the user starts a new turn, so the wake's re-mint resolves
 * `hidden` and nothing revives. The ParkedBrowserChip is the re-entry
 * affordance; the agent's own next browser tool fire re-opens automatically
 * through the existing tool-use gate (streamingHandlers → openPanelForTask).
 *
 * Lives in its own module (not browserStreamHandler) because taskStore calls
 * it from sendMessage and browserStreamHandler imports taskStore — importing
 * back would close a module cycle.
 */
import type { Socket } from "socket.io-client";
import { EVENTS } from "@bash/shared";
import { isDesktopShell } from "@/lib/desktop-shell";
import { useBrowserStore } from "../browserStore";

/**
 * Called from taskStore.sendMessage when the user sends a follow-up on an
 * existing task. If that task's browser pane is open but SUSPENDED (frozen
 * frame — nobody is watching a live stream), fold it into the parked chip and
 * release this socket's server-side pane demand.
 *
 * Deliberately narrow:
 *  - suspended only — a LIVE pane means the user is watching; yanking it on
 *    send would be the inverse bug. waking/error panes are mid-gesture and
 *    keep their existing flows (watchdog, retry CTA).
 *  - remote/machine lane only — the desktop shell's local/embedded browser is
 *    the user's own (free) with its own lifecycle.
 *  - demand-release only — BROWSER_CLOSE_SESSION never tears down sandbox
 *    Chrome (see the orchestrator's handler comment); reopening is instant.
 */
export function foldSuspendedBrowserOnSend(
  socket: Socket | null,
  taskId: string
): void {
  if (isDesktopShell()) return;
  const store = useBrowserStore.getState();
  if (store.viewSnapshot?.state !== "suspended") return;
  if (!store.panelOpenTaskIds.has(taskId)) return;
  const wasLast = store.parkPanelForTask(taskId);
  if (wasLast && socket) socket.emit(EVENTS.BROWSER_CLOSE_SESSION, {});
}
